/**
 * Which Velora Modern side-nav modules follow the selected patient
 * (patient/current.ts). With a patient selected, these items open that
 * patient's page; with none, their usual list or picker. The familiar modes
 * do the same through `moduleBar.patientRoutes` (classicModel.tileHref) and
 * the `patient.open.*` verbs.
 *
 * Every target is an existing, audited patient route: opening it is the same
 * request (and the same `audit_event`) as opening it by hand. Nothing is
 * prefetched; the links are plain navigations.
 *
 * Not followed, on purpose:
 * - Messages: `?patient=` opens or creates a conversation (a write),
 *   which a nav click must not do. The patient file's Text button does it.
 * - Forms, Recall, Reminders, Reviews, Leads, Campaigns, Claims, Insurance:
 *   work lists with no single-patient page.
 * - Schedule, Home, Analytics, Settings: not about one patient.
 */

/** Nav item id (`<module>:<route>`) to the page for one patient (`$patientId`). */
export const PATIENT_NAV_ROUTES: Readonly<Record<string, string>> = {
  'patients:list': '/patients/$patientId',
  'chart:chart': '/patients/$patientId/chart',
  'ledger:billing': '/patients/$patientId/account',
  'txplan:plans': '/patients/$patientId/treatment',
  'perio:worklist': '/patients/$patientId/perio',
  'notes:due': '/patients/$patientId/notes',
  'imaging:viewer': '/imaging?patient=$patientId',
  'frontdesk:patient': '/frontdesk/patient?patient=$patientId',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The route pattern (path only) behind a follow target. */
export function followPath(target: string): string {
  return target.split('?')[0] ?? target;
}

/**
 * The href a nav item opens: the selected patient's page when the item
 * follows the patient and `canOpen` allows its route, else `fallback`.
 */
export function patientNavHref(
  navId: string,
  fallback: string,
  patientId: string | null,
  canOpen: (routePath: string) => boolean,
): string {
  if (!patientId || !UUID.test(patientId)) return fallback;
  const target = PATIENT_NAV_ROUTES[navId];
  if (!target || !canOpen(followPath(target))) return fallback;
  return target.replace('$patientId', encodeURIComponent(patientId));
}

/** `/imaging?patient=x` as router arguments (`to` and `search`). */
export function splitHref(href: string): { to: string; search?: Record<string, string> } {
  const i = href.indexOf('?');
  if (i < 0) return { to: href };
  return {
    to: href.slice(0, i),
    search: Object.fromEntries(new URLSearchParams(href.slice(i + 1))),
  };
}

/** Paths whose `?patient=` names the patient on screen. */
const BY_QUERY = Object.values(PATIENT_NAV_ROUTES)
  .filter((t) => t.includes('?patient='))
  .map(followPath);
const PATIENT_PATH = /^\/patients\/([0-9a-f-]{36})(?:\/|$)/i;

/** The patient a URL opens (`/patients/<id>/...`, `/imaging?patient=<id>`), if any. */
export function patientInUrl(
  pathname: string,
  search: Readonly<Record<string, unknown>>,
): string | null {
  const m = PATIENT_PATH.exec(pathname);
  if (m && UUID.test(m[1] ?? '')) return m[1] ?? null;
  if (BY_QUERY.includes(pathname)) {
    const v = search['patient'];
    if (typeof v === 'string' && UUID.test(v)) return v;
  }
  return null;
}
